import React, { useState, useMemo, useRef } from "react";
import Section from "./layouts/Section";
import emailjs from "@emailjs/browser";
import {
  DEVICE_TYPES,
  MODELS_BY_DEVICE,
  ISSUE_TYPES,
} from "../data/quoteOptions";
import { track, EVENTS } from "../utils/analytics";

/**
 * Guided Repair-Quote flow (Growth Platform — Slice S2).
 *
 * A 4-step wizard: Device -> Model -> Issue -> Contact. Replaces the
 * free-text contact form as the primary conversion path for repair leads,
 * capturing structured data and emitting analytics at each step.
 *
 * Submission: reuses the EXISTING EmailJS pipeline (same service/template/
 * key as ContactUs.js) — no backend required for this slice. The payload is
 * assembled in one place (buildSubmission) so it can be repointed at a MERN
 * /api/quotes endpoint later with zero UI change.
 */

const ACCENT = "#ffaa19";
const NAVY = "#18305d";

const STEPS = ["Device", "Model", "Issue", "Contact"];

function RepairQuote() {
  const [step, setStep] = useState(0);
  const [startedTracked, setStartedTracked] = useState(false);
  const [data, setData] = useState({
    device: "",
    model: "",
    modelCustom: "",
    service: "",
    name: "",
    email: "",
    phone: "",
    message: "",
  });
  const [status, setStatus] = useState({ type: "", message: "" });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const cardRef = useRef(null);

  const models = useMemo(
    () => MODELS_BY_DEVICE[data.device] || [],
    [data.device]
  );

  const resolvedModel = data.modelCustom.trim() || data.model;

  const update = (patch) => setData((d) => ({ ...d, ...patch }));

  // Fire "quote started" exactly once, on the first meaningful interaction.
  const markStarted = () => {
    if (!startedTracked) {
      track(EVENTS.REPAIR_QUOTE_STARTED, { source: "quote_page" });
      setStartedTracked(true);
    }
  };

  const goToStep = (next) => {
    setStep(next);
    track(EVENTS.REPAIR_QUOTE_STEP, {
      step_index: next,
      step_name: STEPS[next],
    });
    if (typeof window !== "undefined" && cardRef.current) {
      // Scroll the wizard card into view (offset below the sticky header)
      // instead of jumping to the very top of the page.
      const y =
        cardRef.current.getBoundingClientRect().top + window.scrollY - 120;
      window.scrollTo({ top: y, behavior: "smooth" });
    }
  };

  const handleDevicePick = (device) => {
    markStarted();
    update({ device, model: "", modelCustom: "" });
    goToStep(1);
  };

  const handleModelPick = (model) => {
    update({ model });
    goToStep(2);
  };

  const handleIssuePick = (service) => {
    update({ service });
    goToStep(3);
  };

  const buildSubmission = () => {
    const summary = [
      `New Repair Quote Request`,
      ``,
      `Device: ${data.device}`,
      `Model: ${resolvedModel || "Not specified"}`,
      `Issue: ${data.service}`,
      `Phone: ${data.phone || "Not provided"}`,
      ``,
      `Details: ${data.message || "None"}`,
    ].join("\n");

    // Matches the existing EmailJS template variable contract
    // (name, email, reply_to, device, subject, message).
    return {
      name: data.name,
      email: data.email,
      reply_to: data.email,
      device: `${data.device}${resolvedModel ? " — " + resolvedModel : ""}`,
      subject: `Quote Request: ${data.service}`,
      message: summary,
    };
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setStatus({ type: "", message: "" });

    emailjs
      .send("service_f4grfuj", "template_bjg4ob4", buildSubmission(), {
        publicKey: "LyFrQSP0J8xcnoKSH",
      })
      .then(
        () => {
          track(EVENTS.REPAIR_QUOTE_COMPLETED, {
            device: data.device,
            service: data.service,
          });
          setSubmitted(true);
          setIsSubmitting(false);
        },
        (error) => {
          track(EVENTS.REPAIR_QUOTE_FAILED, { device: data.device });
          setStatus({
            type: "error",
            message:
              "Something went wrong. Please try again or call us directly at (215) 939-3933.",
          });
          setIsSubmitting(false);
          // eslint-disable-next-line no-console
          console.log("FAILED...", error?.text);
        }
      );
  };

  const canSubmit = data.name.trim() && data.email.trim();

  return (
    <Section title={"Get a Free Repair Quote"}>
      <section className="section page-content">
        <div className="container">
          <div className="row justify-content-center">
            <div className="col-lg-8">
              {!submitted && (
                <div className="text-center mb-30">
                  <h2 className="text-dark mb-10">Get a Free Repair Quote</h2>
                  <p className="text-muted" style={{ maxWidth: "520px", margin: "0 auto" }}>
                    Answer a few quick questions and we'll send you a free,
                    no-obligation estimate — usually within an hour.
                  </p>
                </div>
              )}
              {!submitted ? (
                <div
                  ref={cardRef}
                  style={{
                    background: "#ffffff",
                    padding: "30px",
                    borderRadius: "16px",
                    boxShadow: "0 4px 24px rgba(0,0,0,0.08)",
                  }}
                >
                  <StepBar step={step} onBack={() => goToStep(step - 1)} />

                  {step === 0 && (
                    <StepDevice onPick={handleDevicePick} selected={data.device} />
                  )}

                  {step === 1 && (
                    <StepModel
                      models={models}
                      device={data.device}
                      modelCustom={data.modelCustom}
                      onCustom={(v) => update({ modelCustom: v })}
                      onPick={handleModelPick}
                      onContinue={() => goToStep(2)}
                    />
                  )}

                  {step === 2 && (
                    <StepIssue onPick={handleIssuePick} selected={data.service} />
                  )}

                  {step === 3 && (
                    <StepContact
                      data={data}
                      update={update}
                      resolvedModel={resolvedModel}
                      status={status}
                      isSubmitting={isSubmitting}
                      canSubmit={canSubmit}
                      onSubmit={handleSubmit}
                    />
                  )}
                </div>
              ) : (
                <SuccessCard data={data} resolvedModel={resolvedModel} />
              )}
            </div>
          </div>
        </div>
      </section>
    </Section>
  );

  function StepBar({ step, onBack }) {
    return (
      <div className="mb-30">
        <div className="d-flex align-items-center justify-content-between mb-15">
          <div style={{ minWidth: "70px" }}>
            {step > 0 && (
              <button
                type="button"
                onClick={onBack}
                className="btn btn-link p-0"
                style={{ color: NAVY, fontWeight: 600, textDecoration: "none" }}
              >
                <i className="fas fa-arrow-left mr-2" />
                Back
              </button>
            )}
          </div>
          <span style={{ color: "#888", fontSize: "14px", fontWeight: 600 }}>
            Step {step + 1} of {STEPS.length}
          </span>
        </div>
        <div style={{ display: "flex", gap: "6px" }}>
          {STEPS.map((label, i) => (
            <div
              key={label}
              title={label}
              style={{
                flex: 1,
                height: "6px",
                borderRadius: "3px",
                background: i <= step ? ACCENT : "#e6e6e6",
                transition: "background 0.3s",
              }}
            />
          ))}
        </div>
      </div>
    );
  }
}

/* ---------- Step 1: Device ---------- */
function StepDevice({ onPick, selected }) {
  return (
    <div>
      <h3 className="text-dark mb-10">What device needs a repair?</h3>
      <p className="text-muted mb-25">
        Get a free, no-obligation estimate — usually within 1 hour.
      </p>
      <div className="row">
        {DEVICE_TYPES.map((d) => (
          <div className="col-6 col-md-4 mb-20" key={d.value}>
            <OptionCard
              icon={d.icon}
              label={d.label}
              active={selected === d.value}
              onClick={() => onPick(d.value)}
            />
          </div>
        ))}
      </div>
    </div>
  );
}

/* ---------- Step 2: Model ---------- */
function StepModel({ models, device, modelCustom, onCustom, onPick, onContinue }) {
  return (
    <div>
      <h3 className="text-dark mb-10">Which {device} model?</h3>
      <p className="text-muted mb-25">
        Pick the closest match, or type your exact model below.
      </p>
      <div className="row">
        {models.map((m) => (
          <div className="col-6 col-md-4 mb-15" key={m}>
            <OptionCard label={m} small onClick={() => onPick(m)} />
          </div>
        ))}
      </div>
      <div className="form-group mt-15">
        <label className="mb-2 font-w-600">Or enter exact model (optional)</label>
        <div className="d-flex" style={{ gap: "10px" }}>
          <input
            className="form-control py-3 px-3"
            type="text"
            placeholder="e.g. iPhone 14 Pro Max"
            value={modelCustom}
            onChange={(e) => onCustom(e.target.value)}
          />
          <button
            type="button"
            className="btn btn-primary"
            style={{ whiteSpace: "nowrap", padding: "0 22px" }}
            onClick={onContinue}
          >
            Continue
          </button>
        </div>
      </div>
    </div>
  );
}

/* ---------- Step 3: Issue ---------- */
function StepIssue({ onPick, selected }) {
  return (
    <div>
      <h3 className="text-dark mb-10">What's the problem?</h3>
      <p className="text-muted mb-25">Select the issue that best describes it.</p>
      <div className="row">
        {ISSUE_TYPES.map((it) => (
          <div className="col-6 col-md-4 mb-20" key={it.service + it.label}>
            <OptionCard
              icon={it.icon}
              label={it.label}
              active={selected === it.service}
              onClick={() => onPick(it.service)}
            />
          </div>
        ))}
      </div>
    </div>
  );
}

/* ---------- Step 4: Contact ---------- */
function StepContact({
  data,
  update,
  resolvedModel,
  status,
  isSubmitting,
  canSubmit,
  onSubmit,
}) {
  return (
    <form onSubmit={onSubmit}>
      <h3 className="text-dark mb-10">Almost done — where do we send your quote?</h3>

      <div
        className="mb-25 p-3"
        style={{
          background: "#f6f8fc",
          borderRadius: "10px",
          border: "1px solid #e3e9f5",
        }}
      >
        <strong style={{ color: NAVY }}>Your request:</strong>{" "}
        <span className="text-muted">
          {data.device}
          {resolvedModel ? ` (${resolvedModel})` : ""} — {data.service}
        </span>
      </div>

      {status.message && (
        <div
          style={{
            padding: "15px 20px",
            borderRadius: "8px",
            marginBottom: "20px",
            background: "#f8d7da",
            color: "#721c24",
            border: "1px solid #f5c6cb",
          }}
        >
          <i className="fas fa-exclamation-circle mr-2" />
          {status.message}
        </div>
      )}

      <div className="row">
        <div className="form-group mb-20 col-md-6">
          <label className="mb-2 font-w-600">Name *</label>
          <input
            className="form-control py-3 px-3"
            type="text"
            required
            placeholder="Your name"
            value={data.name}
            onChange={(e) => update({ name: e.target.value })}
          />
        </div>
        <div className="form-group mb-20 col-md-6">
          <label className="mb-2 font-w-600">Email *</label>
          <input
            className="form-control py-3 px-3"
            type="email"
            required
            placeholder="Your email"
            value={data.email}
            onChange={(e) => update({ email: e.target.value })}
          />
        </div>
        <div className="form-group mb-20 col-md-6">
          <label className="mb-2 font-w-600">Phone (optional)</label>
          <input
            className="form-control py-3 px-3"
            type="tel"
            placeholder="(215) 555-1234"
            value={data.phone}
            onChange={(e) => update({ phone: e.target.value })}
          />
        </div>
        <div className="form-group mb-20 col-md-12">
          <label className="mb-2 font-w-600">Anything else? (optional)</label>
          <textarea
            className="form-control p-3"
            rows="4"
            placeholder="Describe the issue, when it happened, etc."
            value={data.message}
            onChange={(e) => update({ message: e.target.value })}
          />
        </div>
      </div>

      <button
        className="btn btn-primary"
        type="submit"
        disabled={isSubmitting || !canSubmit}
        style={{ padding: "14px 35px" }}
      >
        {isSubmitting ? (
          <>
            <i className="fas fa-spinner fa-spin mr-2" />
            Sending...
          </>
        ) : (
          <>
            <i className="fas fa-paper-plane mr-2" />
            Get My Free Quote
          </>
        )}
      </button>
    </form>
  );
}

/* ---------- Success ---------- */
function SuccessCard({ data, resolvedModel }) {
  return (
    <div
      style={{
        background: "#ffffff",
        padding: "40px 30px",
        borderRadius: "16px",
        boxShadow: "0 4px 24px rgba(0,0,0,0.08)",
        textAlign: "center",
      }}
    >
      <div
        style={{
          width: "72px",
          height: "72px",
          borderRadius: "50%",
          background: "#d4edda",
          color: "#155724",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          margin: "0 auto 20px",
          fontSize: "34px",
        }}
      >
        <i className="fas fa-check" />
      </div>
      <h3 className="text-dark mb-10">Quote request sent!</h3>
      <p className="text-muted mb-25" style={{ maxWidth: "460px", margin: "0 auto 25px" }}>
        Thanks {data.name.split(" ")[0] || "there"}! We received your request for{" "}
        <strong>
          {data.device}
          {resolvedModel ? ` (${resolvedModel})` : ""} — {data.service}
        </strong>
        . We'll email you a free estimate shortly, usually within 1 hour during
        business hours.
      </p>
      <a
        href="tel:+12159393933"
        className="btn btn-primary"
        style={{ padding: "12px 30px" }}
        onClick={() => track(EVENTS.PHONE_CALL_CLICKED, { source: "quote_success" })}
      >
        <i className="fas fa-phone-alt mr-2" />
        Prefer to talk? Call (215) 939-3933
      </a>
    </div>
  );
}

/* ---------- Reusable option card ---------- */
function OptionCard({ icon, label, active, onClick, small }) {
  const [hover, setHover] = useState(false);
  return (
    <button
      type="button"
      onClick={onClick}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        width: "100%",
        height: "100%",
        minHeight: small ? "56px" : "110px",
        padding: small ? "14px" : "18px 12px",
        borderRadius: "12px",
        border: `2px solid ${active ? ACCENT : hover ? "#c9d4ea" : "#e6e6e6"}`,
        background: active ? "#fff9ef" : "#ffffff",
        cursor: "pointer",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: "8px",
        transition: "border-color 0.2s, box-shadow 0.2s",
        boxShadow: hover ? "0 4px 14px rgba(0,0,0,0.08)" : "none",
      }}
    >
      {icon && (
        <i
          className={icon}
          style={{ fontSize: "26px", color: active ? ACCENT : NAVY }}
        />
      )}
      <span
        style={{
          fontWeight: 600,
          fontSize: small ? "14px" : "15px",
          color: NAVY,
          textAlign: "center",
          lineHeight: 1.3,
        }}
      >
        {label}
      </span>
    </button>
  );
}

export default RepairQuote;
